import React from 'react';
import RadioGroup from '../../../widgets/RadioGroup';
import { Typography } from '../../../widgets/Typography';
import { IndustryStandardFilterKey, IndustryStandardType } from '../constants';
import Select from '../../../widgets/Select';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';
import { useDeviceType } from '../../../../utilities/common/deviceType';
import useFilters from '../../../../utilities/hooks/useFilters';
import { DownloadIcon } from '../../../../assets/icons/Download';
import AssetURLs from '../../../../shared/constants/assets';

export default function IndustryStandardFilter() {
  const { configData } = useFilterData();
  const { benchmarkType, setBenchmarkType } = useFilters();
  const isMobile = useDeviceType();

  const initialIndustryFilter = configData?.industries?.[0]?.id;

  const [selectedFilter, setSelectedFilter] =
    React.useState<IndustryStandardFilterKey>(
      IndustryStandardFilterKey.INDUSTRY,
    );

  const [selectedFilterValue, setSelectedFilterValue] = React.useState<
    number | null
  >(initialIndustryFilter || null);

  const industrySectors =
    configData?.industries.filter(
      (industry) => industry.meta.type === IndustryStandardType.SECTOR,
    ) || [];

  const industrySizes =
    configData?.industries.filter(
      (industry) => industry.meta.type === IndustryStandardType.SIZE,
    ) || [];

  const currentOrgIndustrySize = industrySizes.find(
    ({ meta: { headCountRange } }) => {
      const [min, max] = headCountRange;
      const totalHeadCount = configData?.totalHeadCount || 0;

      return totalHeadCount >= min && totalHeadCount <= max;
    },
  );

  const currentIndustrySector = industrySectors.find((industry) => {
    return industry.id === configData?.industryId;
  });

  React.useEffect(() => {
    const industry = configData?.industries.find((industry) => {
      return industry.id === benchmarkType;
    });

    if (industry) {
      if (industry.meta.type === IndustryStandardType.SECTOR) {
        setSelectedFilter(IndustryStandardFilterKey.INDUSTRY);
        setSelectedFilterValue(industry.id);
      } else if (industry.meta.type === IndustryStandardType.SIZE) {
        setSelectedFilter(IndustryStandardFilterKey.SIZE);
        setSelectedFilterValue(industry.id);
      }
    }
  }, [benchmarkType]);

  return (
    <div className="mb-6 flex flex-row items-center justify-between">
      <div className="flex flex-row items-center gap-4">
        <Typography variant="body-md" weight="semibold">
          Compare by:
        </Typography>
        <RadioGroup<IndustryStandardFilterKey>
          direction={isMobile ? 'column' : 'row'}
          options={[
            {
              label:
                selectedFilter === IndustryStandardFilterKey.INDUSTRY
                  ? 'Industry:'
                  : 'Industry',
              value: IndustryStandardFilterKey.INDUSTRY,
              content:
                selectedFilter === IndustryStandardFilterKey.INDUSTRY ? (
                  <Select
                    options={industrySectors.map((industry) => ({
                      label: industry.name,
                      value: industry.id,
                    }))}
                    value={selectedFilterValue}
                    onChange={setBenchmarkType}
                    renderOption={(op) => (
                      <div className="flex flex-row items-center md:w-[28rem]">
                        {op.label}
                        {op.value === currentIndustrySector?.id && (
                          <div className="ml-2 rounded-3xl bg-blue-50 px-3 py-0.5 text-sm font-medium text-blue-700">
                            Your organisation
                          </div>
                        )}
                      </div>
                    )}
                  />
                ) : null,
            },
            {
              label:
                selectedFilter === IndustryStandardFilterKey.SIZE
                  ? 'Size:'
                  : 'Size',
              value: IndustryStandardFilterKey.SIZE,
              content:
                selectedFilter === IndustryStandardFilterKey.SIZE ? (
                  <Select
                    options={industrySizes.map((industry) => ({
                      label: industry.name,
                      value: industry.id,
                    }))}
                    value={selectedFilterValue}
                    onChange={setBenchmarkType}
                    renderOption={(op) => (
                      <div className="flex w-80 flex-row items-center">
                        {op.label}
                        {op.value === currentOrgIndustrySize?.id && (
                          <div className="ml-2 rounded-3xl bg-blue-50 px-3 py-0.5 text-sm font-medium text-blue-700">
                            Your organisation
                          </div>
                        )}
                      </div>
                    )}
                  />
                ) : null,
            },
          ]}
          name="industry-standard-filter"
          value={selectedFilter}
          onChange={setSelectedFilter}
        />
      </div>
      <a
        href={AssetURLs.IndustryStandardFAQ}
        target="_blank"
        rel="noopener noreferrer"
        className="cursor-pointer text-blue-700"
      >
        <Typography
          variant="body-sm"
          weight="medium"
          color="primary"
          className="mr-1 inline-block"
        >
          Learn more about Industry Standard
        </Typography>
        <DownloadIcon className="inline-block h-lh" />
      </a>
    </div>
  );
}
